import { Sides } from "@project/emerge-types";
import * as THREE from "three";
import { ALIGNMENT, Alignment } from "../enum/Alignment";
import { OrientationOnRect } from "../enum/OrientationOnRect";
import { cutoutSize } from "../structure/cutoutSelection";
import { RectPropertiesBySide } from "../structure/RectProperties";
import type { AddableMarginDragRanges } from "./addableMarginDragRange";
import { roundToDragGrid, snapMarginToGridInRange } from "./dragGrid";

export {
  DEFAULT_DRAG_GRID_STEP_MM,
  FINE_DRAG_GRID_STEP_MM,
} from "./dragGrid";

/** Settings may store legacy numeric alignment (0/1/2) or string Alignment. */
export function normalizeAddableAlignment(value: unknown): Alignment {
  if (value === Alignment.CENTER || value === ALIGNMENT.CENTER || value === 1) {
    return Alignment.CENTER;
  }
  if (value === Alignment.START || value === ALIGNMENT.START || value === 0) {
    return Alignment.START;
  }
  if (value === Alignment.END || value === ALIGNMENT.END || value === 2) {
    return Alignment.END;
  }
  if (
    value === Alignment.START ||
    value === Alignment.CENTER ||
    value === Alignment.END
  ) {
    return value;
  }
  return Alignment.CENTER;
}

/**
 * Cut size used to keep a part inside its drag rectangle.
 * Catalog addables carry it on `partSelection`. An Ausschnitt has no article,
 * so the size lives on the option as `width` / `height` (read through
 * `cutoutSize`, as the build step does). Ignoring that shifts the hole by half
 * its size: one edge stops short of the drag border and the opposite edge
 * crosses it.
 */
export function readAddableCutSize(option: {
  partSelection?: unknown;
  width?: unknown;
  height?: unknown;
}): { cutX?: number; cutY?: number } {
  const stored = option.partSelection as
    | { cutX?: unknown; cutY?: unknown }
    | undefined;
  const cutX = typeof stored?.cutX === "number" ? stored.cutX : undefined;
  const cutY = typeof stored?.cutY === "number" ? stored.cutY : undefined;
  if (cutX !== undefined || cutY !== undefined) {
    return { cutX, cutY };
  }
  const size = cutoutSize(option);
  return size ? { cutX: size.width, cutY: size.height } : {};
}

function getCut(
  orientation: number,
  orientationOnRect: OrientationOnRect,
  cutX?: number,
  cutY?: number,
): number | undefined {
  const a = orientation % 180 !== 0;
  const b = orientationOnRect === OrientationOnRect.HORIZONTAL;

  if (a !== b) {
    return cutX ? cutX / 2 : undefined;
  }
  return cutY ? cutY / 2 : undefined;
}

/** Signed Position.margin offset (inverse of resolvePosition1d + compose). */
export function positionMarginOffsetOnAxis(
  point: THREE.Vector3,
  resolveMid: number,
  dir: THREE.Vector3,
  axis: "x" | "y" | "z",
): number {
  const dirComponent = dir[axis];
  if (Math.abs(dirComponent) < 1e-9) {
    return 0;
  }
  const resolveScalar = point.dot(dir);
  return (resolveScalar - resolveMid) / dirComponent;
}

export function readResolveAxisMids(
  settingsContext: Record<string, unknown>,
): { horizontal: number; vertical: number } | null {
  const horizontal = settingsContext.horizontalResolveMid;
  const vertical = settingsContext.verticalResolveMid;
  if (typeof horizontal !== "number" || typeof vertical !== "number") {
    return null;
  }
  return { horizontal, vertical };
}

/** Inverse of AddableBuildStep.updateSettingsFromDrag calculateMargin (center path). */
export function marginFromDragOffset(
  offset: number,
  size: number,
  count: number,
  n: number,
  padding: number,
  alignmentCtx: Alignment,
  mirrored: boolean,
): number {
  const pos = offset + size / 2;
  if (count > 1 || alignmentCtx !== Alignment.CENTER) {
    const denom = 1 - 2 * n;
    if (Math.abs(denom) < 0.001) {
      return 0;
    }
    const newMargin = (pos - size * n - padding * denom) / denom;
    return Math.max(0, newMargin);
  }
  return mirrored ? -offset : offset;
}

/** Forward inverse: margin → signed offset from logical rect center. */
export function dragOffsetFromMargin(
  margin: number,
  size: number,
  count: number,
  n: number,
  padding: number,
  alignmentCtx: Alignment,
  mirrored: boolean,
): number {
  if (count > 1 || alignmentCtx !== Alignment.CENTER) {
    const denom = 1 - 2 * n;
    if (Math.abs(denom) < 0.001) {
      return 0;
    }
    const pos = margin * denom + size * n + padding * denom;
    return pos - size / 2;
  }
  return mirrored ? -margin : margin;
}

/**
 * Signed horizontal/vertical offsets from a drag position, matching
 * AddableBuildStep.updateSettingsFromDrag.
 */
export function addableDragOffsetsFromPosition(
  newPosition: THREE.Vector3,
  settingsContext: Record<string, unknown>,
  orientation: number,
): { horizontalOffset: number; verticalOffset: number } | null {
  const side = settingsContext.side as Sides;
  const rectProps = RectPropertiesBySide[side];
  if (!rectProps) {
    return null;
  }

  const { horizontalDir, verticalDir, horizontal, vertical } = rectProps;
  const resolveMids = readResolveAxisMids(settingsContext);

  if (resolveMids) {
    let horizontalOffset = positionMarginOffsetOnAxis(
      newPosition,
      resolveMids.horizontal,
      horizontalDir,
      horizontal,
    );
    if (horizontal === "z" && side === Sides.LEFT && orientation % 180 !== 0) {
      horizontalOffset = -horizontalOffset;
    }
    const verticalOffset = positionMarginOffsetOnAxis(
      newPosition,
      resolveMids.vertical,
      verticalDir,
      vertical,
    );
    return { horizontalOffset, verticalOffset };
  }

  const logicalRectCenter = new THREE.Vector3(
    ...(settingsContext.logicalRectCenter as [number, number, number]),
  );

  const offsetFromCenter = newPosition.clone().sub(logicalRectCenter);
  let horizontalOffset = offsetFromCenter.dot(horizontalDir);
  if (
    rectProps.horizontal === "z" &&
    side === Sides.LEFT &&
    orientation % 180 !== 0
  ) {
    horizontalOffset = -horizontalOffset;
  }
  const verticalOffset = offsetFromCenter.dot(verticalDir);

  return { horizontalOffset, verticalOffset };
}

/**
 * Rebuild a part position from gridded margin offsets (inverse of reverse drag math).
 */
export function addablePositionFromDragOffsets(
  basePosition: THREE.Vector3,
  horizontalOffset: number,
  verticalOffset: number,
  settingsContext: Record<string, unknown>,
  orientation: number,
): THREE.Vector3 | null {
  const side = settingsContext.side as Sides;
  const rectProps = RectPropertiesBySide[side];
  if (!rectProps) {
    return null;
  }

  const { horizontalDir, verticalDir, horizontal, vertical } = rectProps;
  const resolveMids = readResolveAxisMids(settingsContext);

  if (resolveMids) {
    let hOff = horizontalOffset;
    if (horizontal === "z" && side === Sides.LEFT && orientation % 180 !== 0) {
      hOff = -hOff;
    }
    const pH = resolveMids.horizontal + hOff * horizontalDir[horizontal];
    const pV = resolveMids.vertical + verticalOffset * verticalDir[vertical];
    const pos = new THREE.Vector3();
    pos.add(horizontalDir.clone().multiplyScalar(pH));
    pos.add(verticalDir.clone().multiplyScalar(pV));
    pos[rectProps.normal] = basePosition[rectProps.normal];
    return pos;
  }

  const logicalRectCenter = new THREE.Vector3(
    ...(settingsContext.logicalRectCenter as [number, number, number]),
  );

  let hOff = horizontalOffset;
  if (
    rectProps.horizontal === "z" &&
    side === Sides.LEFT &&
    orientation % 180 !== 0
  ) {
    hOff = -hOff;
  }

  const pos = basePosition.clone();
  const targetDotH = logicalRectCenter.dot(horizontalDir) + hOff;
  const targetDotV = logicalRectCenter.dot(verticalDir) + verticalOffset;
  pos.addScaledVector(horizontalDir, targetDotH - pos.dot(horizontalDir));
  pos.addScaledVector(verticalDir, targetDotV - pos.dot(verticalDir));
  return pos;
}

export interface AddableMarginGridResult {
  horizontalMargin: number;
  verticalMargin: number;
  snappedPosition: THREE.Vector3;
}

/**
 * Snap addable drag margins to a grid and return the matching 3D position.
 */
export function snapAddableDragToMarginGrid(
  newPosition: THREE.Vector3,
  settingsContext: Record<string, unknown>,
  option: {
    orientation?: number;
    partSelection?: unknown;
    width?: unknown;
    height?: unknown;
  },
  gridStepMm: number,
  marginRanges?: AddableMarginDragRanges | null,
): AddableMarginGridResult | null {
  const offsets = addableDragOffsetsFromPosition(
    newPosition,
    settingsContext,
    option.orientation ?? 0,
  );
  if (!offsets) {
    return null;
  }

  const side = settingsContext.side as Sides;
  const rectProps = RectPropertiesBySide[side];
  if (!rectProps) {
    return null;
  }

  const logicalRectBoundsMin = new THREE.Vector3(
    ...(settingsContext.logicalRectBoundsMin as [number, number, number]),
  );
  const logicalRectBoundsMax = new THREE.Vector3(
    ...(settingsContext.logicalRectBoundsMax as [number, number, number]),
  );
  const horizontalCount = (settingsContext.horizontalCount as number) ?? 1;
  const verticalCount = (settingsContext.verticalCount as number) ?? 1;
  const horizontalN = (settingsContext.horizontalN as number) ?? 0;
  const verticalN = (settingsContext.verticalN as number) ?? 0;
  const horizontalAlignmentCtx = normalizeAddableAlignment(
    settingsContext.horizontalAlignment,
  );
  const verticalAlignmentCtx = normalizeAddableAlignment(
    settingsContext.verticalAlignment,
  );
  const mirrored = (settingsContext.mirrored as boolean) ?? false;

  const rectSize = logicalRectBoundsMax.clone().sub(logicalRectBoundsMin);
  const horizontalSize = Math.abs(rectSize.dot(rectProps.horizontalDir));
  const verticalSize = Math.abs(rectSize.dot(rectProps.verticalDir));

  const { cutX, cutY } = readAddableCutSize(option);
  const paddingX =
    getCut(option.orientation ?? 0, OrientationOnRect.HORIZONTAL, cutX, cutY) ||
    0;
  const paddingY =
    getCut(option.orientation ?? 0, OrientationOnRect.VERTICAL, cutX, cutY) ||
    0;

  const horizontalMargin = marginFromDragOffset(
    offsets.horizontalOffset,
    horizontalSize,
    horizontalCount,
    horizontalN,
    paddingX,
    horizontalAlignmentCtx,
    mirrored,
  );
  const verticalMargin = marginFromDragOffset(
    offsets.verticalOffset,
    verticalSize,
    verticalCount,
    verticalN,
    paddingY,
    verticalAlignmentCtx,
    mirrored,
  );

  const snapMargin = (margin: number, range?: { min: number; max: number }) => {
    if (!range) {
      return roundToDragGrid(margin, gridStepMm);
    }
    const lo = Math.ceil(Math.min(range.min, range.max));
    const hi = Math.floor(Math.max(range.min, range.max));
    if (hi < lo) {
      return Math.round((range.min + range.max) / 2);
    }
    return snapMarginToGridInRange(margin, lo, hi, gridStepMm);
  };

  const roundedHorizontalMargin = marginRanges
    ? snapMargin(horizontalMargin, marginRanges.horizontal)
    : snapMargin(horizontalMargin);
  const roundedVerticalMargin = marginRanges
    ? snapMargin(verticalMargin, marginRanges.vertical)
    : snapMargin(verticalMargin);

  const griddedHorizontalOffset = dragOffsetFromMargin(
    roundedHorizontalMargin,
    horizontalSize,
    horizontalCount,
    horizontalN,
    paddingX,
    horizontalAlignmentCtx,
    mirrored,
  );
  const griddedVerticalOffset = dragOffsetFromMargin(
    roundedVerticalMargin,
    verticalSize,
    verticalCount,
    verticalN,
    paddingY,
    verticalAlignmentCtx,
    mirrored,
  );

  const snappedPosition = addablePositionFromDragOffsets(
    newPosition,
    griddedHorizontalOffset,
    griddedVerticalOffset,
    settingsContext,
    option.orientation ?? 0,
  );
  if (!snappedPosition) {
    return null;
  }

  return {
    horizontalMargin: roundedHorizontalMargin,
    verticalMargin: roundedVerticalMargin,
    snappedPosition,
  };
}

/** Rebuild a part position from snapped margin values (inverse drag path). */
export function rebuildAddablePositionFromMargins(
  basePosition: THREE.Vector3,
  settingsContext: Record<string, unknown>,
  option: {
    orientation?: number;
    partSelection?: unknown;
    width?: unknown;
    height?: unknown;
  },
  horizontalMargin: number,
  verticalMargin: number,
): THREE.Vector3 | null {
  const side = settingsContext.side as Sides;
  const rectProps = RectPropertiesBySide[side];
  if (!rectProps) {
    return null;
  }

  const logicalRectBoundsMin = new THREE.Vector3(
    ...(settingsContext.logicalRectBoundsMin as [number, number, number]),
  );
  const logicalRectBoundsMax = new THREE.Vector3(
    ...(settingsContext.logicalRectBoundsMax as [number, number, number]),
  );
  const horizontalCount = (settingsContext.horizontalCount as number) ?? 1;
  const verticalCount = (settingsContext.verticalCount as number) ?? 1;
  const horizontalN = (settingsContext.horizontalN as number) ?? 0;
  const verticalN = (settingsContext.verticalN as number) ?? 0;
  const horizontalAlignmentCtx = normalizeAddableAlignment(
    settingsContext.horizontalAlignment,
  );
  const verticalAlignmentCtx = normalizeAddableAlignment(
    settingsContext.verticalAlignment,
  );
  const mirrored = (settingsContext.mirrored as boolean) ?? false;

  const rectSize = logicalRectBoundsMax.clone().sub(logicalRectBoundsMin);
  const horizontalSize = Math.abs(rectSize.dot(rectProps.horizontalDir));
  const verticalSize = Math.abs(rectSize.dot(rectProps.verticalDir));

  const { cutX, cutY } = readAddableCutSize(option);
  const paddingX =
    getCut(option.orientation ?? 0, OrientationOnRect.HORIZONTAL, cutX, cutY) ||
    0;
  const paddingY =
    getCut(option.orientation ?? 0, OrientationOnRect.VERTICAL, cutX, cutY) ||
    0;

  const griddedHorizontalOffset = dragOffsetFromMargin(
    horizontalMargin,
    horizontalSize,
    horizontalCount,
    horizontalN,
    paddingX,
    horizontalAlignmentCtx,
    mirrored,
  );
  const griddedVerticalOffset = dragOffsetFromMargin(
    verticalMargin,
    verticalSize,
    verticalCount,
    verticalN,
    paddingY,
    verticalAlignmentCtx,
    mirrored,
  );

  return addablePositionFromDragOffsets(
    basePosition,
    griddedHorizontalOffset,
    griddedVerticalOffset,
    settingsContext,
    option.orientation ?? 0,
  );
}
